import { ProductSizes } from '@api/returns/types'
import Dropdown, { DropdownProps } from '@components/ui/formElements/Dropdown'
import { Values, Sizes, ExchangesSizeOption } from '@components/returns/types'
import { topsSizeList } from './data'
import styles from './styles.module.scss'
import trackError from '@helpers/trackError'
type SelectedValue = { [id: string]: string | number }

type SizeValues = Sizes[keyof Sizes]
type List = Pick<DropdownProps<SizeValues>, 'list'>['list']

type DropdownSizeOptionsListProps = {
  sizeOptions: ExchangesSizeOption
  setSelection: (args: {
    sizes: Values['sizes'] & { available?: boolean }
  }) => void
  sizes: Partial<ProductSizes>
  values: Values
}

type TopsSize = keyof typeof topsSizeList
type TopsCut = keyof (typeof topsSizeList)['S']
type TopsLength = keyof (typeof topsSizeList)['S']['Straight']

const checkLengthAvailabilityUtil = (
  selectedValue: SelectedValue,
  values: Values
) => {
  try {
    if (Object.keys(values.sizes).length !== 0) {
      const { cut, length, size } = values.sizes as {
        cut: TopsCut
        length: TopsLength
        size: TopsSize
      }
      let topSizes = size && cut && length && topsSizeList[size]

      if (topSizes) {
        let checkLengthForSizeAndCut = cut && length && topSizes[cut][length]

        const selection = Object.keys(selectedValue).toString()

        switch (selection) {
          case 'size':
            topSizes = size && topsSizeList[selectedValue.size as TopsSize]
            checkLengthForSizeAndCut = cut && length && topSizes[cut][length]
            break

          case 'cut':
            topSizes = size && topsSizeList[size]
            checkLengthForSizeAndCut =
              cut && length && topSizes[selectedValue.cut as TopsCut][length]
            break

          case 'length':
            topSizes = size && topsSizeList[size]
            checkLengthForSizeAndCut =
              cut && length && topSizes[cut][selectedValue.length as TopsLength]
            break

          default:
            return { sizes: values.sizes, available: true }
        }
        return { sizes: values.sizes, available: checkLengthForSizeAndCut }
      }

      return { sizes: values.sizes, available: true }
    }
  } catch (error) {
    trackError(new Error('Failed to build returns modal sizes form'), {
      sizes: values.sizes,
      error: error as Error
    })
    return { sizes: values.sizes, available: false }
  }

  return { sizes: values.sizes, available: false }
}

const updateLengthList = (
  values: Values,
  currentValue: string
): List[number] => {
  const { available } = checkLengthAvailabilityUtil(
    {
      length: currentValue
    },
    values
  )

  return {
    value: currentValue,
    label: currentValue,
    availability: available
  }
}

function DropdownSizeOptionsList({
  sizeOptions,
  setSelection,
  sizes,
  values
}: DropdownSizeOptionsListProps) {
  const updateSize = (selectedValue: SelectedValue) => {
    const { sizes, available } = checkLengthAvailabilityUtil(
      selectedValue,
      values
    )
    return setSelection({
      sizes: { ...sizes, available, ...selectedValue }
    })
  }
  const list = sizeOptions.answers.map(currentValue => {
    if (/Short|Regular|Long/.test(currentValue)) {
      return updateLengthList(values, currentValue)
    }

    const value = currentValue.replace('Build ', '')

    return { value, label: value }
  })

  const defaultValue = sizes && sizes[sizeOptions.id]
  const shouldBeInline = sizeOptions.id === 'taper' || sizeOptions.id === 'leg'

  return (
    <div
      className={`${styles.returnsForm__element} ${
        shouldBeInline ? styles.inline : ''
      }`}
    >
      <p className={styles.returnsForm__label}>{sizeOptions.question}</p>
      <Dropdown
        defaultValue={defaultValue || ''}
        classes="dropdown--wide dropdown--left dropdown__returns"
        selectedClasses={`${
          values && !values.sizes[sizeOptions.id] && 'dropdown--grey'
        }`}
        optionClasses="list__option--left list__option--returns"
        list={list}
        selection={values && values.sizes[sizeOptions.id]}
        setSelection={updateSize}
        id={sizeOptions.id}
      />
    </div>
  )
}

export default DropdownSizeOptionsList
